import type { useTranslations } from 'next-intl';
import { faqNodeId, pageNodeId } from '@/src/lib/seo/json-ld';
import type { PropertyDetailListing } from '@/src/components/features/property/types';

export type FaqAnswerType = 'answer' | 'unorderedList' | 'orderedList';

export type FaqQuestion = {
  question: string;
  answer: string | Array<string>;
  answerType?: FaqAnswerType;
  listHeader?: string;
  /**
   * What to publish as the schema answer, when it must differ from the visible
   * one. The accordion may deflect to the property manager; a language model
   * quotes whatever it is given, so the published answer carries only the part
   * backed by data.
   */
  schemaAnswer?: string | Array<string> | null;
};

const NUMBER_WORDS: Record<number, string> = {
  1: 'one',
  2: 'two',
  3: 'three',
  4: 'four',
  5: 'five',
  6: 'six',
  7: 'seven',
  8: 'eight',
  9: 'nine',
  10: 'ten',
};

const numberAsWord = (n: number): string => NUMBER_WORDS[n] ?? String(n);

const asCurrency = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;

type CondensedAmenitiesShape = {
  pets?: string | null;
  laundry?: string | null;
  parking?: boolean | null;
};

type ModelPricingShape = {
  numBeds?: number;
  quantity?: number;
  lowPrice?: number;
};

type DistrictShape = {
  fullName?: string;
};

/**
 * Builds the FAQ question set for an active HDP listing.
 *
 * Returns an empty array when the listing has too little data to populate
 * the schema-template defaults — callers should treat that as "no FAQ".
 */
export const getHdpFaqQuestions = (
  listing: PropertyDetailListing | null | undefined,
  t: ReturnType<typeof useTranslations<'hdp'>>,
  { locale = 'en', updatedAgo }: { locale?: string; updatedAgo?: string } = {},
): Array<FaqQuestion> => {
  if (!listing || listing.active === false) return [];

  const isHouse = listing.propertyType === 'house';
  const buildingName = listing.displayName;
  const condensed = (listing.amenities as unknown as { condensedAmenities?: CondensedAmenitiesShape } | undefined)
    ?.condensedAmenities;
  const petPolicy = condensed?.pets?.toLowerCase();
  const laundry = condensed?.laundry?.toLowerCase();
  const petKeys = {
    'cats and dogs allowed': 'faq.petsCatsAndDogs',
    'cats allowed': 'faq.petsCats',
    'dogs allowed': 'faq.petsDogs',
  } as const;
  const laundryKeys = {
    'in unit': 'faq.laundryInUnit',
    shared: 'faq.laundryShared',
    'on site': 'faq.laundryShared',
  } as const;
  const petKey = petPolicy ? petKeys[petPolicy as keyof typeof petKeys] : undefined;
  const laundryKey = laundry ? laundryKeys[laundry as keyof typeof laundryKeys] : undefined;
  const formattedPet = locale === 'en' ? petPolicy : petKey ? t(petKey) : undefined;
  const formattedLaundry = locale === 'en' ? laundry : laundryKey ? t(laundryKey) : undefined;
  const parking = condensed?.parking;
  const city = listing.address?.city;
  const neighborhood = (listing.address as { neighborhood?: string } | undefined)?.neighborhood;
  const districts = (listing as { areas?: { districts?: Array<DistrictShape> } }).areas?.districts;
  const schoolDistrict = districts?.[0]?.fullName;
  const lastUpdatedAgo =
    updatedAgo ??
    (locale === 'en'
      ? (listing as { history?: { lastUpdated?: { ago?: string } } }).history?.lastUpdated?.ago
      : undefined);
  const modelsAndPricing =
    listing.faqModelsAndPricing ?? (listing as { modelsAndPricing?: Array<ModelPricingShape> }).modelsAndPricing;
  const formattedUnits =
    modelsAndPricing
      ?.filter((unit) => (unit.quantity ?? 0) > 0)
      .map((unit) => {
        const beds = unit.numBeds ?? 0;
        const qty = unit.quantity ?? 0;
        const bedString =
          beds === 0
            ? t('faq.studio', { count: qty })
            : t('faq.bedroomApartment', { count: qty, beds: locale === 'en' ? numberAsWord(beds) : String(beds) });
        const priceTail = unit.lowPrice
          ? t(qty === 1 ? 'faq.price' : 'faq.startingPrice', { price: asCurrency(unit.lowPrice) })
          : '.';
        return t('faq.unitsAvailable', { count: qty, apartment: bedString, priceTail });
      }) ?? [];
  const questions: Array<FaqQuestion> = [];
  const kind = isHouse ? 'house' : 'building';

  if (!isHouse && formattedUnits.length > 0) {
    questions.push({ question: t('faq.availabilityQuestion'), answerType: 'unorderedList', answer: formattedUnits });
  }
  if (buildingName) {
    const petAnswer = formattedPet ? t('faq.petAnswer', { pets: formattedPet, name: buildingName }) : null;
    questions.push({
      question: t('faq.petQuestion', { kind }),
      answer: petAnswer
        ? t('faq.petAnswerWithContact', { answer: petAnswer })
        : t('faq.petContact', { name: buildingName }),
      schemaAnswer: petAnswer,
    });
    const laundryAnswer =
      laundry === 'none'
        ? t('faq.noLaundryAnswer', { name: buildingName })
        : formattedLaundry
          ? t('faq.laundryAnswer', { laundry: formattedLaundry })
          : null;
    questions.push({
      question: t('faq.laundryQuestion', { kind }),
      answer: laundryAnswer ?? t('faq.laundryContact', { name: buildingName }),
      schemaAnswer: laundryAnswer,
    });
    const parkingAnswer = parking ? t('faq.parkingAnswer', { name: buildingName }) : null;
    questions.push({
      question: t('faq.parkingQuestion', { kind }),
      answer: parkingAnswer ?? t('faq.parkingContact', { name: buildingName }),
      schemaAnswer: parkingAnswer,
    });
    if (city && neighborhood) {
      questions.push({
        question: t('faq.neighborhoodQuestion', { city, name: buildingName }),
        answer: t('faq.neighborhoodAnswer', { neighborhood }),
      });
    }
    const districtAnswer = schoolDistrict
      ? t('faq.districtAnswer', { name: buildingName, district: schoolDistrict })
      : null;
    questions.push({
      question: t('faq.districtQuestion', { kind }),
      answer: districtAnswer
        ? t('faq.districtAnswerWithDetails', { answer: districtAnswer })
        : t('faq.districtContact', { name: buildingName }),
      schemaAnswer: districtAnswer,
    });
    if (lastUpdatedAgo) {
      questions.push({
        question: t('faq.updatedQuestion'),
        answer: t('faq.updatedAnswer', { relative: lastUpdatedAgo }),
      });
    }
  }
  return questions;
};

const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Answers that deflect to a human carry no information for an answer engine. */
const DEFLECTION =
  /please contact|contact the property manager|contact the landlord|check with property manager|please check with/i;

/**
 * A question is publishable when its answer is backed by data. `schemaAnswer`
 * overrides the visible answer where the two must differ; otherwise the visible
 * answer is published, unless it deflects to a human.
 */
const schemaQuestions = (questions: Array<FaqQuestion>): Array<FaqQuestion> =>
  questions
    .filter((q) => q.schemaAnswer !== null)
    .map((q) => ({ ...q, answer: q.schemaAnswer ?? q.answer }))
    .filter((q) => {
      const answer = Array.isArray(q.answer) ? q.answer.join(' ') : q.answer;
      return Boolean(answer) && !DEFLECTION.test(answer);
    });

const formatAnswerForSchema = (q: FaqQuestion): string => {
  const type = q.answerType ?? 'answer';
  if (type === 'answer') return q.answer as string;

  const items = Array.isArray(q.answer) ? q.answer : [];
  const ListEl = type === 'unorderedList' ? 'ul' : 'ol';
  const lis = items.map((item) => `<li>${escapeHtml(item)}</li>`).join('');
  const list = `<${ListEl}>${lis}</${ListEl}>`;
  return q.listHeader ? `${q.listHeader}${list}` : list;
};

/**
 * The FAQ ships as its own script rather than inside the page graph: on the HDP
 * the module is backend-gated, and on the SRP it renders behind a Suspense
 * boundary that must not gate the shell. `pageUrl` links it back to that page's
 * node so the graph still connects.
 */
export const buildFaqJsonLd = (questions: Array<FaqQuestion>, pageUrl?: string) => {
  const publishable = schemaQuestions(questions);
  // An absent FAQPage is valid; one with an empty mainEntity is an error, the
  // same way an empty BreadcrumbList is (HP-9034).
  if (publishable.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    ...(pageUrl ? { '@id': faqNodeId(pageUrl), isPartOf: { '@id': pageNodeId(pageUrl) } } : {}),
    mainEntity: publishable.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: formatAnswerForSchema(q),
      },
    })),
  };
};
